/**
 * Pure helpers for expense split computation.
 * No Firestore, no side effects — testable in isolation.
 */

import type { ExpenseEntry, ExpenseSplitEntry, SplitMode } from "./types";

interface SplitMember {
  userId: string;
  userName: string;
}

// Round to 2 decimal places without floating-point drift.
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Split an amount equally among N members, rounded to 2 decimals, with any
 * rounding drift added to the LAST member so the sum is exact to the cent.
 *
 * Example: 100 / 3 = 33.33 · 33.33 · 33.34
 */
export function computeEqualSplits(
  amount: number,
  members: SplitMember[],
  currency: string,
): ExpenseSplitEntry[] {
  const n = members.length;
  if (n === 0) return [];

  const share = round2(amount / n);
  const splits: ExpenseSplitEntry[] = members.map((m) => ({
    userId: m.userId,
    userName: m.userName,
    amount: share,
    percentage: round2(100 / n),
    currency,
  }));

  // Add rounding drift to the last split so the total is exact.
  const rounded = share * n;
  const drift = round2(amount - rounded);
  if (drift !== 0 && splits.length > 0) {
    const last = splits[splits.length - 1]!;
    splits[splits.length - 1] = {
      ...last,
      amount: round2(last.amount + drift),
    };
  }
  return splits;
}

/**
 * Split an amount by explicit percentages. `percentages[i]` corresponds to
 * `members[i]`; the two arrays must be the same length and sum to 100
 * (±0.01 tolerance for user input rounding). Rounding drift is added to
 * the largest split so the sum is exact.
 *
 * Throws when percentages don't sum to 100 or arrays are misaligned —
 * callers should validate on input (Zod schema) before calling.
 */
export function computePercentSplits(
  amount: number,
  members: SplitMember[],
  percentages: number[],
  currency: string,
): ExpenseSplitEntry[] {
  if (members.length !== percentages.length) {
    throw new Error("percentages length must match members length");
  }
  if (members.length === 0) return [];
  const sum = percentages.reduce((a, b) => a + b, 0);
  if (Math.abs(sum - 100) > 0.01) {
    throw new Error(`percentages must sum to 100, got ${sum}`);
  }

  const splits: ExpenseSplitEntry[] = members.map((m, i) => ({
    userId: m.userId,
    userName: m.userName,
    amount: round2((amount * percentages[i]!) / 100),
    percentage: round2(percentages[i]!),
    currency,
  }));

  distributeDrift(splits, amount);
  return splits;
}

/**
 * Split an amount by share weights — like percentages but not required to
 * sum to any specific number. `shares[i] / totalShares` is the fraction
 * that member gets. Useful for "she ate double" scenarios: shares [1, 1, 2]
 * on a €40 bill gives €10 · €10 · €20.
 */
export function computeSharesSplits(
  amount: number,
  members: SplitMember[],
  shares: number[],
  currency: string,
): ExpenseSplitEntry[] {
  if (members.length !== shares.length) {
    throw new Error("shares length must match members length");
  }
  if (members.length === 0) return [];
  const total = shares.reduce((a, b) => a + b, 0);
  if (total <= 0) {
    throw new Error("shares must include at least one positive value");
  }

  const splits: ExpenseSplitEntry[] = members.map((m, i) => ({
    userId: m.userId,
    userName: m.userName,
    amount: round2((amount * shares[i]!) / total),
    percentage: round2((shares[i]! / total) * 100),
    currency,
  }));

  distributeDrift(splits, amount);
  return splits;
}

/**
 * Take exact amounts as-is. `amounts[i]` corresponds to `members[i]`.
 * Validates that they sum to `amount` within 1 cent; throws otherwise.
 * Useful when the receipt itemises each person's charge exactly.
 */
export function computeExactSplits(
  amount: number,
  members: SplitMember[],
  amounts: number[],
  currency: string,
): ExpenseSplitEntry[] {
  if (members.length !== amounts.length) {
    throw new Error("amounts length must match members length");
  }
  if (members.length === 0) return [];
  const sum = amounts.reduce((a, b) => a + b, 0);
  if (Math.abs(sum - amount) > 0.01) {
    throw new Error(`amounts must sum to ${amount}, got ${sum}`);
  }

  return members.map((m, i) => ({
    userId: m.userId,
    userName: m.userName,
    amount: round2(amounts[i]!),
    // Percentages are derived only when a positive total lets us compute them.
    percentage: amount > 0 ? round2((amounts[i]! / amount) * 100) : null,
    currency,
  }));
}

/**
 * Nudge the largest split by the rounding drift so the sum equals amount
 * to the cent. Mutates in place — callers already own the array. Called
 * by every non-exact helper above (equal uses its own last-member drift
 * strategy, kept for backward compat with the existing test snapshot).
 */
function distributeDrift(splits: ExpenseSplitEntry[], amount: number): void {
  if (splits.length === 0) return;
  const sum = splits.reduce((a, s) => a + s.amount, 0);
  const drift = round2(amount - sum);
  if (drift === 0) return;
  // Add drift to whichever split has the largest absolute amount — that
  // way the rounding correction lands where it's least noticeable.
  let idx = 0;
  for (let i = 1; i < splits.length; i++) {
    if (Math.abs(splits[i]!.amount) > Math.abs(splits[idx]!.amount)) idx = i;
  }
  splits[idx] = { ...splits[idx]!, amount: round2(splits[idx]!.amount + drift) };
}

/**
 * Validate that a set of split amounts sums to the expense total,
 * allowing 1-cent rounding tolerance. Returns true when balanced.
 * Works across all split modes since it only inspects the final amounts.
 */
export function splitsBalance(
  splits: readonly ExpenseSplitEntry[],
  expectedTotal: number,
): boolean {
  const sum = splits.reduce((acc, s) => acc + s.amount, 0);
  return Math.abs(sum - expectedTotal) < 0.01;
}

/**
 * Resolve who actually paid for an expense being saved from the form.
 *
 * Precedence:
 *   1. An explicit pick from the payer picker (must be a trip member).
 *   2. On edit, the payer already stored on the row (`payerUid ?? creatorId`).
 *   3. On create, the current user.
 *
 * Step 2 matters: editing someone else's expense (fixing a typo, changing
 * the category) must never silently re-credit the payment to the editor.
 * That bug made users "owe" members who never paid for anything.
 */
export function resolveExpensePayer(opts: {
  selectedPayerUid: string | null | undefined;
  existing: Pick<ExpenseEntry, "creatorId" | "creatorName" | "payerUid" | "payerName"> | null | undefined;
  currentUser: SplitMember;
  members: readonly SplitMember[];
}): SplitMember {
  const { selectedPayerUid, existing, currentUser, members } = opts;
  const member = (uid: string) => members.find((m) => m.userId === uid);

  if (selectedPayerUid) {
    const m = member(selectedPayerUid);
    if (m) return { userId: m.userId, userName: m.userName };
  }
  if (existing) {
    const uid = existing.payerUid ?? existing.creatorId;
    const m = member(uid);
    return {
      userId: uid,
      userName: m?.userName ?? existing.payerName ?? existing.creatorName,
    };
  }
  const me = member(currentUser.userId);
  return me ? { userId: me.userId, userName: me.userName } : currentUser;
}

/**
 * The `payerUid` / `payerName` fields to persist. Omitted when the payer is
 * the creator — old-row semantics (`payerUid ?? creatorId`) cover that case.
 */
export function payerFieldsFor(
  payer: SplitMember,
  creatorId: string,
): Pick<ExpenseEntry, "payerUid" | "payerName"> {
  return payer.userId === creatorId
    ? { payerUid: undefined, payerName: undefined }
    : { payerUid: payer.userId, payerName: payer.userName };
}

/**
 * Who an expense is split between when saving from the form.
 *
 * New expenses split across every current trip member. Edits keep the
 * expense's original participants — re-splitting an old dinner across
 * whoever is on the trip *today* would charge people who joined later and
 * silently drop people who left. Non-equal modes also list current members
 * who weren't on the original split (appended, defaulting to 0) so the
 * editor can deliberately include them.
 */
export function splitParticipants(
  existing: Pick<ExpenseEntry, "splits"> | null | undefined,
  members: readonly SplitMember[],
  mode: SplitMode,
): SplitMember[] {
  if (!existing || existing.splits.length === 0) return [...members];
  const nameOf = (uid: string) => members.find((m) => m.userId === uid)?.userName;
  const original = existing.splits
    .filter((s) => mode !== "equal" || s.amount !== 0)
    .map((s) => ({ userId: s.userId, userName: nameOf(s.userId) ?? s.userName }));
  if (mode === "equal") return original;
  const seen = new Set(original.map((m) => m.userId));
  return [...original, ...members.filter((m) => !seen.has(m.userId))];
}
